import React, { useEffect, useRef, useState } from "react";
import { X, Upload, ChevronLeft, ChevronRight, Loader2, Download, Sparkles, GitCompareArrows } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import AiEditHistoryModal from "@/components/AiEditHistoryModal";
import { lockBodyScroll } from "@/lib/lockBodyScroll";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Fotograaf-lightbox voor de fotoreeks. Zelfde topbalk als admin:
 * History (n) · Vergelijk met origineel · Origineel (download) · AI (download) · Vervangen.
 */
export default function PhotographerPhotoLightbox({
    requestId,
    photos,
    previousUrls = {},
    openIndex,
    onClose,
    onChanged,
    addressLabel = "",
    onEditWithAI = null,
}) {
    const [idx, setIdx] = useState(openIndex);
    const [uploading, setUploading] = useState(false);
    const [showCompare, setShowCompare] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [aiHistory, setAiHistory] = useState([]);
    const fileRef = useRef(null);

    useEffect(() => { setIdx(openIndex); }, [openIndex]);
    useEffect(() => { setShowCompare(false); }, [idx]);

    useEffect(() => {
        if (idx === null || idx === undefined || !requestId) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/requests/${requestId}/ai-edit-jobs`);
                if (!cancelled) {
                    setAiHistory((data || []).filter((j) => j.photo_index === idx && j.status === "completed" && j.result_url));
                }
            } catch { /* non-blocking */ }
        })();
        return () => { cancelled = true; };
    }, [requestId, idx, photos]);

    useEffect(() => {
        if (idx === null || idx === undefined) return;
        const handler = (e) => {
            if (e.key === "Escape") { if (historyOpen) setHistoryOpen(false); else onClose?.(); }
            else if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + photos.length) % photos.length);
            else if (e.key === "ArrowRight") setIdx((i) => (i + 1) % photos.length);
        };
        window.addEventListener("keydown", handler);
        const release = lockBodyScroll(true);
        return () => { window.removeEventListener("keydown", handler); release(); };
    }, [idx, photos.length, onClose, historyOpen]);

    if (idx === null || idx === undefined || !photos || photos.length === 0) return null;
    if (idx < 0 || idx >= photos.length) return null;

    const slugify = (s) => (s || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s.-]/g, "")
        .replace(/\s+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 60) || "foto";
    const downloadName = (variant) => {
        const addr = slugify(addressLabel);
        const n = String(idx + 1).padStart(2, "0");
        const suffix = variant === "original" ? "origineel" : "SocialFactoryAI";
        return `${addr ? addr + " - " : ""}foto ${n} - ${suffix}.jpg`;
    };

    const currentUrl = photos[idx];
    const originalUrl = previousUrls?.[String(idx)] || null;
    const isAiEdited = !!originalUrl;

    const replaceFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Selecteer een afbeelding (jpg, png, webp...)");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data: up } = await api.post("/upload", fd);
            await api.post(`/requests/${requestId}/ai-photos/${idx}/replace`, { new_photo_url: up.url });
            toast.success("Foto vervangen");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Vervangen mislukt");
        }
        setUploading(false);
    };

    return (
        <div
            className="fixed inset-0 z-[200] bg-black/95 flex flex-col items-center justify-start overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            data-testid="photographer-photo-lightbox"
        >
            <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) replaceFile(f);
                }}
                data-testid="photographer-replace-photo-file"
            />

            {/* Top bar */}
            <div className="sticky top-0 inset-x-0 flex items-center justify-between gap-2 p-3 lg:p-6 z-30 w-full pointer-events-none">
                <div className="text-xs font-mono text-zinc-400 bg-black/60 backdrop-blur-sm px-3 h-9 inline-flex items-center rounded-sm border border-white/10 pointer-events-auto shrink-0">
                    {idx + 1} / {photos.length}
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 pointer-events-auto flex-wrap justify-end">
                    {onEditWithAI && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onEditWithAI(idx); }}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-95"
                            data-testid="photographer-photo-edit-ai-btn"
                            title="Bewerk deze foto met AI — credits worden afgeboekt"
                        >
                            <Sparkles className="h-3.5 w-3.5" /> Bewerk met AI
                        </button>
                    )}
                    {aiHistory.length > 0 && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setHistoryOpen(true); }}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-[#E5A93D]/15 hover:bg-[#E5A93D]/25 text-[#E5A93D] rounded-sm transition backdrop-blur-sm border border-[#E5A93D]/30"
                            data-testid="photographer-photo-ai-history-btn"
                            title={`Open ${aiHistory.length} AI-versie${aiHistory.length === 1 ? "" : "s"} voor deze foto`}
                        >
                            <Sparkles className="h-3.5 w-3.5" /> History ({aiHistory.length})
                        </button>
                    )}
                    {isAiEdited && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setShowCompare((v) => !v); }}
                            className={`inline-flex items-center gap-1.5 text-xs px-3 h-9 rounded-sm transition active:scale-95 backdrop-blur-sm border ${showCompare ? "bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold border-[#E5A93D]" : "bg-zinc-100 hover:bg-zinc-200 text-black border-zinc-200"}`}
                            data-testid="photographer-photo-compare-btn"
                            title="Vergelijk de huidige (AI) versie met het origineel"
                        >
                            <GitCompareArrows className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">{showCompare ? "Sluit vergelijking" : "Vergelijk met origineel"}</span>
                            <span className="sm:hidden">{showCompare ? "Sluit" : "Vergelijk"}</span>
                        </button>
                    )}
                    {isAiEdited && (
                        <a
                            href={`${BACKEND}${originalUrl}?dl=${encodeURIComponent(downloadName("original"))}`}
                            download={downloadName("original")}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition backdrop-blur-sm border border-zinc-200"
                            data-testid="photographer-photo-download-original-btn"
                            title="Download het origineel"
                        >
                            <Download className="h-3.5 w-3.5" /> Origineel
                        </a>
                    )}
                    <a
                        href={`${BACKEND}${currentUrl}?dl=${encodeURIComponent(downloadName(isAiEdited ? "ai" : "original"))}`}
                        download={downloadName(isAiEdited ? "ai" : "original")}
                        onClick={(e) => e.stopPropagation()}
                        className={`inline-flex items-center gap-1.5 text-xs px-3 h-9 rounded-sm transition backdrop-blur-sm border ${isAiEdited ? "bg-[#E5A93D]/15 hover:bg-[#E5A93D]/25 text-[#E5A93D] border-[#E5A93D]/30" : "bg-zinc-100 hover:bg-zinc-200 text-zinc-950 border-zinc-200"}`}
                        data-testid="photographer-photo-download-current-btn"
                        title={isAiEdited ? "Download de AI-bewerkte versie" : "Download deze foto"}
                    >
                        <Download className="h-3.5 w-3.5" /> {isAiEdited ? "AI" : "Foto"}
                    </a>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); if (!uploading) fileRef.current?.click(); }}
                        disabled={uploading}
                        className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="photographer-replace-photo-btn"
                    >
                        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                        {uploading ? "Bezig..." : "Vervangen"}
                    </button>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
                        className="inline-flex items-center justify-center h-9 w-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition backdrop-blur-sm border border-zinc-200"
                        data-testid="photographer-photo-close-btn"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Image stage */}
            <div className="relative flex items-center justify-center w-full px-2 lg:px-6 pb-8" onClick={(e) => e.stopPropagation()}>
                {photos.length > 1 && (
                    <button
                        type="button"
                        onClick={() => setIdx((i) => (i - 1 + photos.length) % photos.length)}
                        className="absolute left-2 lg:left-6 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-12 w-12 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-full transition active:scale-95 backdrop-blur-sm z-10"
                        data-testid="photographer-photo-prev-btn"
                    >
                        <ChevronLeft className="h-6 w-6" />
                    </button>
                )}
                {showCompare && isAiEdited ? (
                    <BeforeAfterSlider
                        beforeUrl={`${BACKEND}${originalUrl}`}
                        afterUrl={`${BACKEND}${currentUrl}`}
                        className="max-h-[78vh] max-w-[92vw]"
                        testId="photographer-photo-compare-slider"
                    />
                ) : (
                    <img
                        src={`${BACKEND}${currentUrl}`}
                        alt={`Foto ${idx + 1}`}
                        className="max-h-[82vh] max-w-[95vw] object-contain rounded-sm"
                        data-testid="photographer-lightbox-image"
                    />
                )}
                {photos.length > 1 && (
                    <button
                        type="button"
                        onClick={() => setIdx((i) => (i + 1) % photos.length)}
                        className="absolute right-2 lg:right-6 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-12 w-12 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-full transition active:scale-95 backdrop-blur-sm z-10"
                        data-testid="photographer-photo-next-btn"
                    >
                        <ChevronRight className="h-6 w-6" />
                    </button>
                )}
            </div>

            <AiEditHistoryModal
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                requestId={requestId}
                photoIndex={idx}
                originalUrl={originalUrl}
                currentUrl={currentUrl}
                jobs={aiHistory}
                addressLabel={addressLabel}
                onActivated={() => { setHistoryOpen(false); onChanged?.(); }}
                onReplaced={() => { onChanged?.(); }}
            />
        </div>
    );
}
